import { useMemo } from 'react';
import {
  ItemStatus,
  Location,
  Order,
  OrderQuery,
  OrdersQuery,
  OrderScope,
  User,
  useItemQuery,
  useItemsQuery,
  useOrderQuery,
  useOrdersQuery,
  useUsersQuery,
  useInventoryQuery,
  InventoryScope,
  OrderRef,
  InventoryQuery,
  Maybe,
} from '.';
import {
  isGlobalItem,
  isLocalItem,
  mapItemCombinations,
  mergeStockWithItems,
} from '@/lib/utils/database';
import { QUERY_LIMIT } from '@/lib/constants';
import { useUser, useUserRoles } from '../features/session/hooks';
import {
  getItemStatus,
  getNextItemStatuses,
} from '@/lib/utils/items-status-machine';
import { checkRole, UserRole } from '@pcmp-checka/lib-utilities/atkomst';
import { SerializedError } from '@reduxjs/toolkit';
import { GqlError } from '@/typings/graphql';
import { isOrderCompleted } from '@/lib/utils/orders';
import { UserInfo } from '@/typings/api';

type WithNextStatus<T> = T & {
  nextUpdatableStatus: ItemStatus[];
};

type InventoryOrderItemWithScope = WithNextStatus<
  InventoryQuery['inventory']['nodes'][number]
>;
type OrderItemWithScope = WithNextStatus<Order['items'][number]>;

export type OrderWithScope = Omit<Order, 'items'> & {
  items: OrderItemWithScope[];
};

export const useItemDetail = (itemId: string) => {
  const { data, ...rest } = useItemQuery({ itemId });
  const item = data?.item;

  return { data: item, ...rest };
};

export const useLocationProducts = (
  location?: Maybe<Location>,
  opts?: {
    onlyInWarehouse?: boolean;
    filter?: 'lendOut' | 'refurbish';
    scope?: 'all' | 'local' | 'global';
    warehouseId?: string;
  }
) => {
  // Hooks
  const locationId = location?._id;
  // Retrieve all items for the location, we will filter them later based on stock and combinations
  const { data, isLoading, error } = useItemsQuery(
    {
      pagination: {
        limit: QUERY_LIMIT,
      },
      // Only pass locationId when available
      locationId: locationId || '',
    },
    {
      // Skip the query until we have a valid locationId
      skip: !locationId,
    }
  );
  // Data
  const { filter, scope = 'all', warehouseId, onlyInWarehouse } = opts || {};
  const productsWithStock = useMemo(() => {
    if (!location) return [];

    const allItems = data?.items.nodes || [];
    let itemsWithStock = allItems.filter((item) =>
      scope === 'all'
        ? true
        : scope === 'local'
        ? isLocalItem(item)
        : isGlobalItem(item)
    );

    // Remove items that doesn't belong to any location's warehouse
    // This is needed to avoid showing items that are not actually available
    // in the location (maybe because they were removed from warehouses by admins)
    if (onlyInWarehouse) {
      itemsWithStock = allItems.filter((item) =>
        location.stock.some(
          (stockItem) =>
            stockItem.item._id === item._id && stockItem.availability.length
        )
      );
    }
    // Instead of dealing with items independently, we want to group them by their product and then calculate
    // stock for each product combination (e.g. different colors or sizes of the same product should be grouped together).
    const itemCombinations = mapItemCombinations(itemsWithStock, location);
    const products = mergeStockWithItems(
      location,
      itemCombinations,
      warehouseId
    );

    return products;
  }, [data, warehouseId, location, onlyInWarehouse, scope]);

  const filteredProducts = useMemo(() => {
    let result = productsWithStock;

    // The filter is applied after grouping items into products, so a product will be included
    // in the result if at least one of its items matches the filter criteria
    if (filter) {
      result = productsWithStock.filter((product) =>
        product.items.some((item) => {
          const key = filter === 'lendOut' ? 'lendOut' : 'refurbish';

          return item.stock[key] > 0;
        })
      );
    }

    return result;
  }, [productsWithStock, filter]);

  return useMemo(
    () => ({ data: filteredProducts, isLoading, error }),
    [filteredProducts, isLoading, error]
  );
};

export const useLocationUsers = (
  locationId: string,
  options?: {
    skip?: boolean;
  }
): {
  data?: {
    users?: User[];
    administrators?: User[];
    externalUsers?: User[];
  };
  isLoading: boolean;
  error: SerializedError | GqlError | undefined;
} => {
  const { data, isLoading, error } = useUsersQuery(
    {
      locationId,
      pagination: {
        limit: QUERY_LIMIT,
      },
    },
    options
  );

  return useMemo(
    () => ({
      data: {
        users: data?.users.nodes,
        administrators: data?.administrators.nodes,
        externalUsers: data?.externalUsers.nodes,
      },
      isLoading,
      error,
    }),
    [data, isLoading, error]
  );
};

const setOrderScope = (
  data:
    | OrdersQuery['orders']['nodes'][number]
    | NonNullable<OrderQuery['order']>,
  user: ReturnType<typeof useUser>,
  roles: UserRole[],
  scope: OrderScope
) => {
  const { items, ...order } = data;

  return {
    ...order,
    items: items.map((item) => {
      const { lastStatus } = item;
      const nextUpdatableStatus = getNextItemStatuses(lastStatus).filter(
        (status) => {
          const entry = getItemStatus(status);

          if (
            user.isManager &&
            scope === OrderScope.Coworkers &&
            entry.updateByManager &&
            order.requestedFor.manager === user.email
          ) {
            return true;
          }

          if (
            scope === OrderScope.Me &&
            entry.updateRole.includes('CHECKA Location Viewer')
          ) {
            return true;
          }

          if (
            [OrderScope.Location, OrderScope.User].includes(scope) &&
            entry.updateRole.includes('CHECKA Order Admin')
          ) {
            return checkRole(
              roles,
              'CHECKA Order Admin',
              order.location.atkomstClientId,
              order.location.country
            );
          }

          return false;
        }
      );

      return {
        ...item,
        nextUpdatableStatus,
      };
    }),
  };
};

export const useOrderByScope = (
  orderId: string,
  scope: OrderScope,
  options?: {
    skip?: boolean;
  }
): {
  data?: OrderWithScope;
  isLoading: boolean;
  error: SerializedError | GqlError | undefined;
} => {
  const user = useUser();
  const roles = useUserRoles();
  const { data, isLoading, error } = useOrderQuery({ orderId }, options);

  const order = useMemo(
    () => data?.order && (setOrderScope(data.order, user, roles, scope) as any), // @TODO: fix any
    [data, roles, scope, user]
  );

  return useMemo(
    () => ({ data: order, isLoading, error }),
    [order, isLoading, error]
  );
};

export const useOrdersByScope = (
  scope: OrderScope,
  value: string,
  options?: {
    locationId?: string;
    skip?: boolean;
  }
): {
  data?: OrderWithScope[];
  isLoading: boolean;
  error: SerializedError | GqlError | undefined;
} => {
  const user = useUser();
  const roles = useUserRoles();
  const { data, isLoading, error } = useOrdersQuery(
    {
      locationId: options?.locationId,
      scope: {
        scope,
        value,
      },
      pagination: {
        limit: QUERY_LIMIT,
      },
    },
    options
  );

  const orders = useMemo(
    () =>
      data?.orders.nodes.map((order) =>
        setOrderScope(order, user, roles, scope)
      ) as any, // @TODO: fix any
    [data, roles, scope, user]
  );

  return useMemo(
    () => ({ data: orders, isLoading, error }),
    [orders, isLoading, error]
  );
};

export const useInventoryByScope = (
  scope: InventoryScope,
  value: string,
  locationId: string,
  options?: {
    skip?: boolean;
  }
): {
  data?: ({
    order: OrderRef;
  } & InventoryOrderItemWithScope)[];
  isLoading: boolean;
  error: SerializedError | GqlError | undefined;
} => {
  // Hooks
  const { data, isLoading, error } = useInventoryQuery(
    {
      scope: {
        scope,
        value,
      },
      locationId,
    },
    options
  );
  // Data
  const inventory = useMemo(
    () =>
      data?.inventory.nodes.map((item) => {
        const { lastStatus } = item;
        const nextUpdatableStatus = getNextItemStatuses(lastStatus).filter(
          (status) => {
            const entry = getItemStatus(status);

            if (scope === InventoryScope.Me) {
              return entry.updateRole.includes('CHECKA Location Viewer');
            }

            if (scope === InventoryScope.User) {
              return entry.updateRole.includes('CHECKA Order Admin');
            }

            return false;
          }
        );

        return {
          ...item,
          nextUpdatableStatus,
        };
      }),
    [data, scope]
  );

  return useMemo(
    () => ({ data: inventory, isLoading, error }),
    [inventory, isLoading, error]
  );
};

export const useGetUserInfoByItemId = (
  itemId: string,
  options?: {
    locationId?: string;
    skip?: boolean;
  }
): {
  data?: UserInfo[];
  isLoading: boolean;
  error: SerializedError | GqlError | undefined;
} => {
  // Hooks
  const {
    data: item,
    isLoading: isLoadingItem,
    error: errorDetail,
  } = useItemDetail(itemId);
  const {
    data: orders,
    isLoading: isLoadingOrders,
    error: errorOrders,
  } = useOrdersByScope(OrderScope.Location, options?.locationId || '', {
    locationId: options?.locationId || '',
    skip: options?.skip || !options?.locationId,
  });
  const itemOrders = useMemo(
    () =>
      orders?.filter((order) =>
        order.items.some((orderItem) => orderItem.item._id === item?._id)
      ),
    [orders, item]
  );
  // Data
  const users = useMemo(() => {
    const userInfoMap = (itemOrders || []).reduce((acc, order) => {
      const user = order.requestedFor;

      acc[user.email] = acc[user.email] || {
        email: user.email,
        displayName: user.displayName,
        manager: user.manager || null,
        roles: null,
        orders: {
          completed: 0,
          pending: 0,
        },
        items: {
          collected: 0,
          pending: 0,
          recalled: 0,
        },
      };

      order.items.forEach((orderItem) => {
        if (orderItem.item._id !== item?._id) return;

        if (isOrderCompleted(order)) {
          acc[user.email].orders.completed++;
        } else {
          acc[user.email].orders.pending++;
        }

        switch (orderItem.lastStatus) {
          case ItemStatus.Pending:
          case ItemStatus.Approved:
          case ItemStatus.Prepared:
            acc[user.email].items.pending++;
            break;
          case ItemStatus.Collected:
            acc[user.email].items.collected++;
            break;
          case ItemStatus.Recalled:
            acc[user.email].items.recalled++;
            break;
        }
      });

      return acc;
    }, {} as Record<string, UserInfo>);

    return Object.values(userInfoMap);
  }, [item, itemOrders]);

  return useMemo(
    () => ({
      data: Object.values(users),
      isLoading: isLoadingItem || isLoadingOrders,
      error: errorDetail || errorOrders,
    }),
    [users, isLoadingItem, isLoadingOrders, errorDetail, errorOrders]
  );
};
